import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { 
  ArrowLeft, 
  ArrowRight,
  Check,
  FileText,
  Users,
  ClipboardList,
  FileCheck,
  BookOpen,
  BarChart3,
  GraduationCap,
  FileBarChart,
  MessageSquare,
  AlertTriangle,
  Save,
  Loader2
} from 'lucide-react';
import { Layout } from '../../components/layout/Layout';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { toast } from 'sonner';
import { API_BASE } from '../../config/api';
import { useAuth } from '../../contexts/AuthContext';

// Import step components
import ProgrammeInfoStep from '../../components/programme/steps/ProgrammeInfoStep';
import LearnersStep from '../../components/programme/steps/LearnersStep';
import RubricStep from '../../components/programme/steps/RubricStep';
import PoliciesStep from '../../components/programme/steps/PoliciesStep';
import ContentBuilderStep from '../../components/programme/steps/ContentBuilderStep';
import ProgressStep from '../../components/programme/steps/ProgressStep';
import GradebookStep from '../../components/programme/steps/GradebookStep';
import ReportsStep from '../../components/programme/steps/ReportsStep';
import ReviewsStep from '../../components/programme/steps/ReviewsStep';
import InterventionStep from '../../components/programme/steps/InterventionStep';

const API = `${API_BASE}/api`;

// Workflow steps configuration
const WORKFLOW_STEPS = [
  { 
    id: 1, 
    key: 'info',
    name: 'Programme Information', 
    description: 'Basic details and metadata',
    icon: FileText, 
    color: 'text-blue-400',
    bgColor: 'bg-blue-500/20'
  },
  { 
    id: 2, 
    key: 'learners',
    name: 'Add Learners', 
    description: 'Enroll participants via CSV/Excel',
    icon: Users, 
    color: 'text-teal-400',
    bgColor: 'bg-teal-500/20'
  },
  { 
    id: 3, 
    key: 'rubric',
    name: 'Grading Rubric', 
    description: 'Upload assessment criteria',
    icon: ClipboardList, 
    color: 'text-amber-400',
    bgColor: 'bg-amber-500/20'
  },
  { 
    id: 4, 
    key: 'policies',
    name: 'Programme Policies', 
    description: 'Add rules and guidelines',
    icon: FileCheck, 
    color: 'text-purple-400',
    bgColor: 'bg-purple-500/20'
  },
  { 
    id: 5, 
    key: 'content',
    name: 'Content Builder', 
    description: 'Create courses and activities',
    icon: BookOpen, 
    color: 'text-indigo-400',
    bgColor: 'bg-indigo-500/20'
  },
  { 
    id: 6, 
    key: 'progress',
    name: 'Monitor Progress', 
    description: 'Track learner advancement',
    icon: BarChart3, 
    color: 'text-cyan-400',
    bgColor: 'bg-cyan-500/20'
  },
  { 
    id: 7, 
    key: 'gradebook',
    name: 'Gradebook', 
    description: 'Grade submissions and POE',
    icon: GraduationCap, 
    color: 'text-emerald-400',
    bgColor: 'bg-emerald-500/20'
  },
  { 
    id: 8, 
    key: 'reports',
    name: 'Reports', 
    description: 'Generate progress reports',
    icon: FileBarChart, 
    color: 'text-orange-400',
    bgColor: 'bg-orange-500/20'
  },
  { 
    id: 9, 
    key: 'reviews',
    name: 'Course Reviews', 
    description: 'View learner feedback',
    icon: MessageSquare, 
    color: 'text-pink-400',
    bgColor: 'bg-pink-500/20'
  },
  { 
    id: 10, 
    key: 'intervention',
    name: 'Learner Intervention', 
    description: 'Manage at-risk learners',
    icon: AlertTriangle, 
    color: 'text-red-400',
    bgColor: 'bg-red-500/20'
  }
];

export default function ProgrammeWorkflowPage() {
  const navigate = useNavigate();
  const { programmeId } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const { token } = useAuth();
  const isEditing = !!programmeId;

  const headers = { Authorization: `Bearer ${token}` };

  // Get current step from URL or default to 1
  const currentStepParam = parseInt(searchParams.get('step') || '1');
  const [currentStep, setCurrentStep] = useState(currentStepParam);
  
  // Workflow state
  const [workflowStatus, setWorkflowStatus] = useState({});
  const [programme, setProgramme] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (isEditing) {
      fetchProgrammeData();
    } else {
      setLoading(false);
    }
  }, [programmeId]);

  useEffect(() => {
    // Sync URL with current step
    setSearchParams({ step: currentStep.toString() });
  }, [currentStep]);

  const fetchProgrammeData = async () => {
    try {
      const [progRes, statusRes] = await Promise.all([
        axios.get(`${API}/programmes/${programmeId}`, { headers }),
        axios.get(`${API}/programmes/${programmeId}/workflow-status`, { headers })
      ]);
      
      setProgramme(progRes.data);
      setWorkflowStatus(statusRes.data);
    } catch (error) {
      console.error('Failed to fetch programme:', error);
      toast.error('Failed to load programme');
    } finally {
      setLoading(false);
    }
  };

  const refreshWorkflowStatus = async () => {
    if (!programmeId) return;
    try {
      const res = await axios.get(`${API}/programmes/${programmeId}/workflow-status`, { headers });
      setWorkflowStatus(res.data);
    } catch (error) {
      console.error('Failed to refresh status:', error);
    }
  };

  const goToStep = (stepId) => {
    setCurrentStep(stepId);
  };

  const nextStep = () => {
    if (currentStep < 10) {
      setCurrentStep(currentStep + 1);
    }
  };

  const prevStep = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  const getStepStatus = (stepId) => {
    const statusKey = `step_${stepId}_${WORKFLOW_STEPS[stepId - 1]?.key}`;
    return workflowStatus[statusKey] || false;
  };

  const renderStepContent = () => {
    const stepProps = {
      programmeId,
      programme,
      token,
      headers,
      onUpdate: fetchProgrammeData,
      onRefreshStatus: refreshWorkflowStatus
    };

    switch (currentStep) {
      case 1:
        return <ProgrammeInfoStep {...stepProps} />;
      case 2:
        return <LearnersStep {...stepProps} />;
      case 3:
        return <RubricStep {...stepProps} />;
      case 4:
        return <PoliciesStep {...stepProps} />;
      case 5:
        return <ContentBuilderStep {...stepProps} />;
      case 6:
        return <ProgressStep {...stepProps} />;
      case 7:
        return <GradebookStep {...stepProps} />;
      case 8:
        return <ReportsStep {...stepProps} />;
      case 9:
        return <ReviewsStep {...stepProps} />;
      case 10:
        return <InterventionStep {...stepProps} />;
      default:
        return <ProgrammeInfoStep {...stepProps} />;
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center min-h-[60vh]">
          <Loader2 className="h-8 w-8 animate-spin text-teal-400" />
        </div>
      </Layout>
    );
  }

  const currentStepConfig = WORKFLOW_STEPS[currentStep - 1];

  return (
    <Layout>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-4">
            <Button 
              variant="ghost" 
              size="sm"
              onClick={() => navigate('/admin/programmes')}
              className="text-zinc-400 hover:text-white"
            >
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back to Programmes
            </Button>
            <div>
              <h1 className="text-2xl font-bold text-white">
                {isEditing ? (programme?.programme_name || 'Edit Programme') : 'Create Programme'}
              </h1>
              <p className="text-sm text-zinc-400">
                Step {currentStep} of 10: {currentStepConfig.name}
              </p>
            </div>
          </div>
          
          {workflowStatus.can_publish && (
            <Badge className="bg-emerald-500/20 text-emerald-400 border-emerald-500/30">
              Ready to Publish
            </Badge>
          )}
        </div>

        {/* Workflow Steps Navigation */}
        <div className="mb-8">
          <div className="flex items-center justify-between overflow-x-auto pb-4">
            {WORKFLOW_STEPS.map((step, idx) => {
              const Icon = step.icon;
              const isActive = currentStep === step.id;
              const isCompleted = getStepStatus(step.id);
              const isAccessible = true; // Flexible navigation
              
              return (
                <React.Fragment key={step.id}>
                  <button
                    onClick={() => isAccessible && goToStep(step.id)}
                    disabled={!isAccessible}
                    className={`flex flex-col items-center min-w-[80px] transition-all ${
                      isAccessible ? 'cursor-pointer' : 'cursor-not-allowed opacity-50'
                    }`}
                  >
                    <div className={`relative w-10 h-10 rounded-full flex items-center justify-center transition-all ${
                      isActive 
                        ? `${step.bgColor} ring-2 ring-offset-2 ring-offset-zinc-900 ring-${step.color.replace('text-', '')}`
                        : isCompleted 
                          ? 'bg-emerald-500/20'
                          : 'bg-zinc-800'
                    }`}>
                      {isCompleted && !isActive ? (
                        <Check className="h-5 w-5 text-emerald-400" />
                      ) : (
                        <Icon className={`h-5 w-5 ${isActive ? step.color : 'text-zinc-500'}`} />
                      )}
                    </div>
                    <span className={`mt-2 text-xs font-medium text-center ${
                      isActive ? 'text-white' : 'text-zinc-500'
                    }`}>
                      {step.name}
                    </span>
                  </button>
                  
                  {idx < WORKFLOW_STEPS.length - 1 && (
                    <div className={`flex-1 h-0.5 mx-2 ${
                      getStepStatus(step.id) ? 'bg-emerald-500/50' : 'bg-zinc-800'
                    }`} />
                  )}
                </React.Fragment>
              );
            })}
          </div>
        </div>

        {/* Step Content */}
        <Card className="bg-zinc-900 border-zinc-800 mb-6">
          <CardHeader className="border-b border-zinc-800">
            <div className="flex items-center gap-3">
              <div className={`p-2 rounded-lg ${currentStepConfig.bgColor}`}>
                {React.createElement(currentStepConfig.icon, { 
                  className: `h-5 w-5 ${currentStepConfig.color}` 
                })}
              </div>
              <div>
                <CardTitle className="text-white">{currentStepConfig.name}</CardTitle>
                <CardDescription>{currentStepConfig.description}</CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent className="p-6">
            {renderStepContent()}
          </CardContent>
        </Card>

        {/* Navigation Footer */}
        <div className="flex items-center justify-between">
          <Button
            variant="outline"
            onClick={prevStep}
            disabled={currentStep === 1}
            className="border-zinc-700 text-zinc-300"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Previous
          </Button>

          <div className="flex items-center gap-2 text-sm text-zinc-500">
            {WORKFLOW_STEPS.map((step) => (
              <button
                key={step.id}
                onClick={() => goToStep(step.id)}
                className={`w-2 h-2 rounded-full transition-all ${
                  currentStep === step.id 
                    ? 'bg-teal-400 w-6' 
                    : getStepStatus(step.id) 
                      ? 'bg-emerald-500'
                      : 'bg-zinc-700'
                }`}
              />
            ))}
          </div>

          <Button
            onClick={nextStep}
            disabled={currentStep === 10}
            className="bg-teal-600 hover:bg-teal-700 text-white"
          >
            Next
            <ArrowRight className="h-4 w-4 ml-2" />
          </Button>
        </div>
      </div>
    </Layout>
  );
}
